import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import { getActiveSearchTerm } from '../utils/searchThreshold';
import '../styles/DocumentApp.css';
import '../styles/DocumentModal.css';
import { formatCurrency as formatCurrencyBySystem, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';

const DocumentCreationModal = ({ isOpen, onClose, documentType, onDocumentSaved, initialDocument = null }) => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [totalTax, setTotalTax] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [editedProducts, setEditedProducts] = useState({});
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [invalidCustomer, setInvalidCustomer] = useState(false);
  const [invalidProducts, setInvalidProducts] = useState(false);
  const [invalidProductFields, setInvalidProductFields] = useState({});
  const formatCurrency = (value) => (
    formatCurrencyBySystem(Number(value || 0), getNumberingSystemFromStorage(), {
      prefix: getCurrencyPrefixFromStorage()
    })
  );
  const currencyPrefix = getCurrencyPrefixFromStorage();

  // Customer search state
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const customerSearchRef = useRef(null);
  const customerSearchInputRef = useRef(null);

  // Product search state
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productSearching, setProductSearching] = useState(false);
  const productSearchRef = useRef(null);
  const productSearchInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setSelectedCustomer(null);
      setSelectedProducts([]);
      setEditedProducts({});
      setTotalTax(0);
      setGrandTotal(0);
      setSaveError("");
      setInvalidCustomer(false);
      setInvalidProducts(false);
      setInvalidProductFields({});
      setCustomerQuery('');
      setCustomerResults([]);
      setProductQuery('');
      setProductResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && initialDocument) {
      const customer = initialDocument.customer || {};
      const customerId = customer._id || initialDocument.customerId;
      if (customerId) {
        setSelectedCustomer({
          _id: customerId,
          name: customer.name || ''
        });
        setCustomerQuery(customer.name || '');
      }

      if (Array.isArray(initialDocument.products)) {
        setSelectedProducts(initialDocument.products.map((product) => ({
          _id: product._id,
          name: product.name,
          code: product.code,
          slno: product.slno,
          description: product.description,
          rate: product.rate ?? product.price,
          price: product.price ?? product.rate,
          tax: product.tax,
          taxType: product.taxType,
          quantity: product.quantity || 1
        })));
      }

      setEditedProducts({});
      setSaveError('');
      setInvalidCustomer(false);
      setInvalidProducts(false);
      setInvalidProductFields({});
    }
  }, [isOpen, initialDocument]);

  const isBlank = (value) => value === undefined || value === null || `${value}`.trim() === '';

  const isMissingProductField = (field, value) => {
    switch (field) {
      case 'description':
      case 'taxType':
        return isBlank(value);
      case 'rate':
      case 'tax':
        return isBlank(value) || Number.isNaN(Number(value));
      case 'quantity':
        return isBlank(value) || Number.isNaN(Number(value)) || Number(value) <= 0;
      default:
        return false;
    }
  };

  // Customer search with debounce
  useEffect(() => {
    const searchCustomers = async () => {
      const activeSearchTerm = getActiveSearchTerm(customerQuery);
      if (!activeSearchTerm) {
        setCustomerSearching(false);
        setCustomerResults([]);
        return;
      }
      
      setCustomerSearching(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/search/customers?query=${encodeURIComponent(activeSearchTerm)}`);
        setCustomerResults(res.data);
      } catch (err) {
        console.error('Error searching customers:', err);
        setCustomerResults([]);
      } finally {
        setCustomerSearching(false);
      }
    };

    const timeoutId = setTimeout(searchCustomers, 300);
    return () => clearTimeout(timeoutId);
  }, [customerQuery]);

  useEffect(() => {
    if (customerResults.length > 0 && !customerSearching && customerSearchInputRef.current) {
      customerSearchInputRef.current.focus();
      const end = customerSearchInputRef.current.value.length;
      customerSearchInputRef.current.setSelectionRange(end, end);
    }
  }, [customerResults.length, customerSearching]);

  // Product search with debounce
  useEffect(() => {
    const searchProducts = async () => {
      const activeSearchTerm = getActiveSearchTerm(productQuery);
      if (!activeSearchTerm) {
        setProductSearching(false);
        setProductResults([]);
        return;
      }
      
      setProductSearching(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/search/products?query=${encodeURIComponent(activeSearchTerm)}`);
        setProductResults(res.data);
      } catch (err) {
        console.error('Error searching products:', err);
        setProductResults([]);
      } finally {
        setProductSearching(false);
      }
    };

    const timeoutId = setTimeout(searchProducts, 300);
    return () => clearTimeout(timeoutId);
  }, [productQuery]);

  useEffect(() => {
    if (productResults.length > 0 && !productSearching && productSearchInputRef.current) {
      productSearchInputRef.current.focus();
      const end = productSearchInputRef.current.value.length;
      productSearchInputRef.current.setSelectionRange(end, end);
    }
  }, [productResults.length, productSearching]);

  const handleAddCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerQuery(customer.name);
    setCustomerResults([]);
    setInvalidCustomer(false);
    setSaveError('');
  };

  const handleAddProduct = (product) => {
    setInvalidProducts(false);
    setSaveError('');
    setSelectedProducts(prevProducts => {
      const existing = prevProducts.find(p => p._id === product._id);
      if (existing) {
        return prevProducts.map(p =>
          p._id === product._id ? { ...p, quantity: p.quantity + 1 } : p
        );
      } else {
        return [...prevProducts, { ...product, quantity: 1 }];
      }
    });
    setProductQuery('');
    setProductResults([]);
  };

  const handleProductFieldChange = (productId, field, value) => {
    setSaveError('');
    setInvalidProductFields(prev => {
      if (!prev[productId]?.[field]) {
        return prev;
      }

      const updatedProductErrors = { ...prev[productId], [field]: isMissingProductField(field, value) };
      const remainingErrors = Object.entries(updatedProductErrors).filter(([, isInvalid]) => isInvalid);
      const next = { ...prev };

      if (remainingErrors.length === 0) {
        delete next[productId];
      } else {
        next[productId] = Object.fromEntries(remainingErrors);
      }

      return next;
    });

    setEditedProducts(prev => {
      const newState = {
        ...prev,
        [productId]: {
          ...(prev[productId] || {}),
          [field]: value,
          isEdited: true
        }
      };

      const { totalTax, grandTotal } = calculateTotals(selectedProducts, newState);
      setTotalTax(totalTax);
      setGrandTotal(grandTotal);
      
      return newState;
    });
  };

  const calculateProductTotals = (product, editedProduct = {}) => {
    const rate = Number(editedProduct.rate ?? product.rate);
    const tax = Number(editedProduct.tax ?? product.tax ?? product["tax "]);
    const quantity = Number(editedProduct.quantity ?? product.quantity) || 1;

    if (!isNaN(rate) && !isNaN(tax) && !isNaN(quantity)) {
      const baseAmount = rate * quantity;
      const taxAmount = (baseAmount * tax) / 100;
      return {
        baseAmount,
        taxAmount,
        total: baseAmount + taxAmount
      };
    }
    return { baseAmount: 0, taxAmount: 0, total: 0 };
  };

  const calculateTotals = (products, edits) => {
    const totals = products.reduce((acc, product) => {
      const { taxAmount, total } = calculateProductTotals(product, edits[product._id]);
      return {
        totalTax: acc.totalTax + taxAmount,
        grandTotal: acc.grandTotal + total
      };
    }, { totalTax: 0, grandTotal: 0 });

    return totals;
  };

  const handleRemoveProduct = (productId) => {
    setInvalidProductFields(prev => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
    setSelectedProducts(prev => prev.filter(p => p._id !== productId));
    setEditedProducts(prev => {
      const newState = { ...prev };
      delete newState[productId];
      return newState;
    });
  };

  const handleSave = async () => {
    setSaveError("");
    setInvalidCustomer(false);
    setInvalidProducts(false);
    setInvalidProductFields({});

    let hasValidationError = false;

    if (!selectedCustomer) {
      setInvalidCustomer(true);
      hasValidationError = true;
    }

    if (selectedProducts.length === 0) {
      setInvalidProducts(true);
      hasValidationError = true;
    }

    const nextInvalidProductFields = {};

    selectedProducts.forEach((product) => {
      const editedProduct = editedProducts[product._id] || {};
      const candidateValues = {
        description: editedProduct.description ?? product.description,
        rate: editedProduct.rate ?? product.rate,
        tax: editedProduct.tax ?? product.tax ?? product["tax "],
        taxType: editedProduct.taxType ?? product.taxType,
        quantity: editedProduct.quantity ?? product.quantity
      };

      const fieldErrors = Object.entries(candidateValues).reduce((acc, [field, value]) => {
        if (isMissingProductField(field, value)) {
          acc[field] = true;
        }
        return acc;
      }, {});

      if (Object.keys(fieldErrors).length > 0) {
        nextInvalidProductFields[product._id] = fieldErrors;
        hasValidationError = true;
      }
    });

    if (hasValidationError) {
      setInvalidProductFields(nextInvalidProductFields);
      setSaveError('Please complete all highlighted required fields before saving.');
      return;
    }
    
    const { totalTax: calculatedTax, grandTotal: calculatedTotal } = calculateTotals(selectedProducts, editedProducts);
    
    try {
      setSaving(true);
      const productsWithEdits = selectedProducts.map(product => {
        const editedProduct = editedProducts[product._id] || {};
        return {
          productId: product._id,
          description: editedProduct.description ?? product.description,
          price: editedProduct.rate ?? product.rate,
          tax: editedProduct.tax ?? product.tax,
          taxType: editedProduct.taxType ?? product.taxType,
          quantity: editedProduct.quantity ?? product.quantity
        };
      });

      const payload = {
        customer: selectedCustomer._id,
        products: productsWithEdits,
        total: calculatedTotal,
        type: documentType
      };
      
      const res = initialDocument?._id
        ? await axios.put(`${API_BASE_URL}/documents/${initialDocument._id}`, payload)
        : await axios.post(`${API_BASE_URL}/documents`, payload);
      
      if (onDocumentSaved) {
        onDocumentSaved(res.data, { mode: initialDocument?._id ? 'update' : 'create' });
      }
      
      onClose();
    } catch (err) {
      console.error('Error saving document:', err);
      const errorMsg = `Failed to save ${documentType.toLowerCase()}: ${err.response?.data?.error || err.message}`;
      setSaveError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const { totalTax, grandTotal } = calculateTotals(selectedProducts, editedProducts);
    setTotalTax(totalTax);
    setGrandTotal(grandTotal);
  }, [selectedProducts]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      overflowY: 'auto'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '900px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        margin: '20px'
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '12px'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '600',
            color: '#1f2937'
          }}>
            Create {documentType}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <div className="invoice-app-container" style={{ padding: 0 }}>
          {/* Customer Search */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151'
            }}>
              Customer
            </label>
            <div ref={customerSearchRef} style={{ position: 'relative' }}>
              <input
                ref={customerSearchInputRef}
                type="text"
                placeholder="Type to search customer..."
                value={customerQuery}
                readOnly={customerSearching}
                onChange={(e) => {
                  if (customerSearching) {
                    return;
                  }
                  setCustomerQuery(e.target.value);
                  if (e.target.value.trim()) {
                    setInvalidCustomer(false);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: invalidCustomer ? '2px solid #dc2626' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  backgroundColor: invalidCustomer ? '#fef2f2' : '#fff',
                  boxShadow: invalidCustomer ? '0 0 0 3px rgba(220, 38, 38, 0.12)' : 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  const nextFocusedElement = e.relatedTarget;
                  if (!customerSearchRef.current || !customerSearchRef.current.contains(nextFocusedElement)) {
                    setCustomerResults([]);
                  }
                }}
              />
              {customerSearching && (
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '12px'
                }}>
                  <span
                    className="spinner-border spinner-border-sm text-secondary"
                    role="status"
                    aria-label="Searching"
                    title="Searching"
                  />
                </div>
              )}
              {customerResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}>
                  {customerResults.map((customer) => (
                    <div
                      key={customer._id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAddCustomer(customer);
                      }}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleAddCustomer(customer);
                        }
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: '500', color: '#1f2937' }}>{customer.name}</div>
                      {customer.gst && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                          GST: {customer.gst}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedCustomer && (
            <div style={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
                Selected Customer
              </div>
              <div style={{ fontSize: '14px', color: '#1f2937' }}>
                <div style={{ marginBottom: '4px' }}><strong>Name:</strong> {selectedCustomer.name}</div>
                {selectedCustomer.address && <div style={{ marginBottom: '4px' }}><strong>Address:</strong> {selectedCustomer.address}</div>}
                {selectedCustomer.gst && <div><strong>GST:</strong> {selectedCustomer.gst}</div>}
              </div>
            </div>
          )}

          {/* Product Search */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '6px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151'
            }}>
              Add Products
            </label>
            <div ref={productSearchRef} style={{ position: 'relative' }}>
              <input
                ref={productSearchInputRef}
                type="text"
                placeholder="Type to search products..."
                value={productQuery}
                readOnly={productSearching}
                onChange={(e) => {
                  if (productSearching) {
                    return;
                  }
                  setProductQuery(e.target.value);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  const nextFocusedElement = e.relatedTarget;
                  if (!productSearchRef.current || !productSearchRef.current.contains(nextFocusedElement)) {
                    setProductResults([]);
                  }
                }}
              />
              {productSearching && (
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '12px'
                }}>
                  <span
                    className="spinner-border spinner-border-sm text-secondary"
                    role="status"
                    aria-label="Searching"
                    title="Searching"
                  />
                </div>
              )}
              {productResults.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}>
                  {productResults.map((product) => (
                    <div
                      key={product._id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAddProduct(product);
                      }}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleAddProduct(product);
                        }
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: '500', color: '#1f2937', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{product.description}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        HSN: {product.hsncode} | Rate: {formatCurrency(product.rate)} | Tax: {product.tax || product["tax "]}%
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="selected-products-section" style={{ 
            marginTop: '20px',
            border: invalidProducts ? '2px solid #dc2626' : 'none',
            borderRadius: invalidProducts ? '8px' : '0',
            padding: invalidProducts ? '8px' : '0',
            backgroundColor: invalidProducts ? '#fef2f2' : 'transparent'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151'
              }}>
                Selected Products ({selectedProducts.length})
              </label>
            </div>
            {selectedProducts.length === 0 ? (
              <div style={{ color: '#6b7280', padding: '12px' }}>No products added.</div>
            ) : (
              <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>#</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Code</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Description</th>
                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Rate ({currencyPrefix})</th>
                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Tax(%)</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Tax Type</th>
                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Quantity</th>
                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Total</th>
                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProducts.map((product, index) => {
                    const editedProduct = editedProducts[product._id] || {};
                    const { total } = calculateProductTotals(product, editedProduct);
                    const productFieldErrors = invalidProductFields[product._id] || {};
                    
                    return (
                      <tr key={product._id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px', verticalAlign: 'top' }}>{index + 1}</td>
                        <td style={{ padding: '8px', verticalAlign: 'top', fontWeight: '500' }}>{product.slno || 'N/A'}</td>
                        <td style={{ padding: '8px', verticalAlign: 'top' }}>
                          <textarea
                            value={editedProduct.description ?? product.description}
                            onChange={(e) => handleProductFieldChange(product._id, 'description', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '4px 8px',
                              border: productFieldErrors.description ? '2px solid #dc2626' : '1px solid #d1d5db',
                              borderRadius: '4px',
                              minHeight: '60px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                              backgroundColor: productFieldErrors.description ? '#fef2f2' : '#fff'
                            }}
                          />
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', verticalAlign: 'top' }}>
                          <input
                            type="number"
                            value={editedProduct.rate ?? product.rate}
                            onChange={(e) => handleProductFieldChange(product._id, 'rate', e.target.value)}
                            style={{
                              width: '100px',
                              padding: '4px 8px',
                              border: productFieldErrors.rate ? '2px solid #dc2626' : '1px solid #d1d5db',
                              borderRadius: '4px',
                              textAlign: 'right',
                              backgroundColor: productFieldErrors.rate ? '#fef2f2' : '#fff'
                            }}
                          />
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', verticalAlign: 'top' }}>
                          <input
                            type="number"
                            value={editedProduct.tax ?? product.tax ?? product["tax "]}
                            onChange={(e) => handleProductFieldChange(product._id, 'tax', e.target.value)}
                            style={{
                              width: '80px',
                              padding: '4px 8px',
                              border: productFieldErrors.tax ? '2px solid #dc2626' : '1px solid #d1d5db',
                              borderRadius: '4px',
                              textAlign: 'right',
                              backgroundColor: productFieldErrors.tax ? '#fef2f2' : '#fff'
                            }}
                          />
                        </td>
                        <td style={{ padding: '8px', textAlign: 'left', verticalAlign: 'top' }}>
                          <select
                            value={editedProduct.taxType ?? product.taxType ?? ''}
                            onChange={(e) => handleProductFieldChange(product._id, 'taxType', e.target.value)}
                            aria-invalid={productFieldErrors.taxType}
                            style={{
                              width: '120px',
                              padding: '4px 8px',
                              border: productFieldErrors.taxType ? '2px solid #dc2626' : '1px solid #d1d5db',
                              borderRadius: '4px',
                              backgroundColor: productFieldErrors.taxType ? '#fef2f2' : '#fff',
                              boxShadow: productFieldErrors.taxType ? '0 0 0 3px rgba(220, 38, 38, 0.12)' : 'none'
                            }}
                          >
                            <option value="" disabled>Select</option>
                            <option value="SGST/CGST">SGST/CGST</option>
                            <option value="IGST">IGST</option>
                          </select>
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', verticalAlign: 'top' }}>
                          <input
                            type="number"
                            value={editedProduct.quantity ?? product.quantity}
                            onChange={(e) => handleProductFieldChange(product._id, 'quantity', e.target.value)}
                            style={{
                              width: '80px',
                              padding: '4px 8px',
                              border: productFieldErrors.quantity ? '2px solid #dc2626' : '1px solid #d1d5db',
                              borderRadius: '4px',
                              textAlign: 'right',
                              backgroundColor: productFieldErrors.quantity ? '#fef2f2' : '#fff'
                            }}
                          />
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500', verticalAlign: 'top' }}>
                          {formatCurrency(total)}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'top' }}>
                          <button
                            onClick={() => handleRemoveProduct(product._id)}
                            style={{
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: '600', backgroundColor: '#f9fafb' }}>
                    <td colSpan="7" style={{ padding: '12px', textAlign: 'right' }}>Tax Total:</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>{formatCurrency(totalTax)}</td>
                    <td></td>
                  </tr>
                  <tr style={{ fontWeight: '700', backgroundColor: '#f3f4f6', fontSize: '16px' }}>
                    <td colSpan="7" style={{ padding: '12px', textAlign: 'right' }}>Grand Total:</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#059669' }}>{formatCurrency(grandTotal)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '24px',
            paddingTop: '20px',
            borderTop: '2px solid #e5e7eb',
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'white'
          }}>
            {saveError && (
              <div style={{
                marginRight: 'auto',
                backgroundColor: '#fff7ed',
                border: '1px solid #fdba74',
                color: '#9a3412',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)'
              }}>
                {saveError}
              </div>
            )}
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: saving ? 'not-allowed' : 'pointer',
                color: '#1f2937'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '10px 20px',
                backgroundColor: saving ? '#9ca3af' : '#059669',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: saving ? 'not-allowed' : 'pointer',
                color: 'white'
              }}
            >
              {saving ? 'Saving...' : `Save ${documentType}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentCreationModal;
